/* eslint-disable max-lines-per-function */
import { Loader } from 'kiswe-ui'
import { KeyDict } from '@/types'
import { CastStatus, CastType, Clip } from '@/types/casts'

export abstract class CastK360Info {
  start_date: Date
  end_date: Date
  status: CastStatus

  mp4: string|null
  m3u8: string|null
  k360_event_id: number|string|null
  k360_event_id_backup: number|string|null
  liveClipping: boolean
  clips: KeyDict<Clip>
  video_start_time?: number = 0
  video_start_time_backup: number = 0

  abstract get numTracks (): number

  constructor (data: unknown) {
    this.start_date = Loader.loadDate(data, 'start_date')
    this.end_date = Loader.loadDate(data, 'end_date')
    this.status = Loader.loadString(data, 'status', CastStatus.Unknown) as CastStatus
    this.mp4 = Loader.loadStringOrNull(data, 'mp4', null)
    this.m3u8 = Loader.loadStringOrNull(data, 'm3u8', null)
    this.k360_event_id = Loader.loadAndCast<number | string>(data, 'k360_event_id', null)
    this.k360_event_id_backup = Loader.loadAndCast<number | string>(data, 'k360_event_id_backup', null)
    this.liveClipping = Loader.loadBoolean(data, 'liveClipping', false)
    this.clips = Loader.loadAndCast<KeyDict<Clip>>(data, 'clips', {})
    // @ts-ignore
    if (data.video_start_time !== undefined) {
      // @ts-ignore
      this.video_start_time = data.video_start_time // This should become a number or null
    }
    this.video_start_time_backup = Loader.loadNumber(data, 'video_start_time_backup', 0)
  }

  public cleanupK360Info () {
    this.status = CastStatus.Unknown
    this.mp4 = null
    this.m3u8 = null
    this.k360_event_id = null
    this.k360_event_id_backup = null
    this.video_start_time = 0
    this.video_start_time_backup = 0
  }
}

export enum ConnectionTabId {
  Connect = 'connect',
  Disconnect = 'disconnect',
  TalkbackCasterToCrew = 'talkback-caster-crew',
  TalkbackCrew = 'talkback-crew',
  TalkbackAll = 'talkback-all',
  SelfMuteActive = 'self-mute-active',
  SelfMuteInactive = 'self-mute-inactive',
  Chat = 'chat',
  ScreenShareStart = 'screen-share-start',
  ScreenShareStop = 'screen-share-stop'
}

export const CAST_TYPE_LABEL: Record<CastType, string> = {
  [CastType.Switcher]: 'Switcher',
  [CastType.FanRoom]: 'Fan room',
  [CastType.StationSwitcher]: 'Station switcher'
}

export enum CrewLocation {
  Continent = 'continent',
  Global = 'global'
}

export const CREW_LOCATION_LABELS: Record<CrewLocation, string> = {
  [CrewLocation.Continent]: '1 Continent',
  [CrewLocation.Global]: 'Global'
}

export class CastDetailXls {
  name: string|null = null
  url: string|null = null
  type: string|null = null
  template: string|null = null
  startDate: string|null = null
  startTime: string|null = null
  endDate: string|null = null
  endTime: string|null = null
  bitRate: number|null = null
  resolution: string|null = null
  frameRate: string|null = null
  crewLocation: string|null = null
  region: string|null = null
  athleteFName: string|null = null
  athleteLName: string|null = null
  gender: string|null = null
  discipline: string|null = null
  country: string|null = null
  venue: string|null = null
  athleteActive: boolean|null = null
  athleteContacted: boolean|null = null
  china: string|null = null
  backup: string|null = null
  backupRegion: string|null = null
  totalGuests: number|null = null
  guestLanguage: string|null = null
  guestName: string = ''
  athleteRelationWGuest: string|null = null
  moderatorNotesOnGuest: string|null = null
  guestLink: string|null = null

  constructor (data: unknown) {
    this.name = Loader.loadStringOrNull(data, 'name', null)
    this.url = Loader.loadStringOrNull(data, 'url', null)
    this.type = Loader.loadStringOrNull(data, 'type', null)
    this.template = Loader.loadStringOrNull(data, 'template', null)
    this.startDate = Loader.loadStringOrNull(data, 'startDate', null)
    this.startTime = Loader.loadStringOrNull(data, 'startTime', null)
    this.endDate = Loader.loadStringOrNull(data, 'endDate', null)
    this.endTime = Loader.loadStringOrNull(data, 'endTime', null)
    this.bitRate = Loader.loadNumberOrNull(data, 'bitRate', null)
    this.resolution = Loader.loadStringOrNull(data, 'resolution', null)
    this.frameRate = Loader.loadStringOrNull(data, 'frameRate', null)
    this.crewLocation = Loader.loadStringOrNull(data, 'crewLocation', null)
    this.region = Loader.loadStringOrNull(data, 'region', null)
    this.athleteFName = Loader.loadStringOrNull(data, 'athleteFName', null)
    this.athleteLName = Loader.loadStringOrNull(data, 'athleteLName', null)
    this.gender = Loader.loadStringOrNull(data, 'gender', null)
    this.discipline = Loader.loadStringOrNull(data, 'discipline', null)
    this.country = Loader.loadStringOrNull(data, 'country', null)
    this.venue = Loader.loadStringOrNull(data, 'venue', null)
    this.athleteActive = Loader.loadBooleanOrNull(data, 'athleteActive', null)
    this.athleteContacted = Loader.loadBooleanOrNull(data, 'athleteContacted', null)
    this.china = Loader.loadStringOrNull(data, 'china', null)
    this.backup = Loader.loadStringOrNull(data, 'backup', null)
    this.backupRegion = Loader.loadStringOrNull(data, 'backupRegion', null)
    this.totalGuests = Loader.loadNumberOrNull(data, 'totalGuests', null)
    this.guestLanguage = Loader.loadStringOrNull(data, 'guestLanguage', null)
    this.guestName = Loader.loadString(data, 'guestName', '')
    this.athleteRelationWGuest = Loader.loadStringOrNull(data, 'athleteRelationWGuest', null)
    this.moderatorNotesOnGuest = Loader.loadStringOrNull(data, 'moderatorNotesOnGuest', null)
    this.guestLink = Loader.loadStringOrNull(data, 'guestLink', null)
  }
}

export interface RegionalInfo {
  chinaProxyUrl?: string
  fixed: boolean,
  rtmpServer?: string,
  rtmpServerBackup?: string,
  wrtcserver: string,
  wrtcServerBackup?: string
}
